import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import "./index.scss";
import { getEnvData, queryClient } from "./utils/services";
import router from "./utils/router";
import reportWebVitals from "./reportWebVitals";

/**
 * Checks if Directus CMS is available by attempting to fetch from the API
 */
async function checkDirectusAvailability(oasysUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch(
      `${oasysUrl}/api/v1/corp/items/corp_info?limit=1`,
      {
        method: "GET",
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    return false;
  }
}

(async () => {
  try {
    const { env, oasys_url } = await getEnvData();

    // Check if Directus CMS is available
    if (oasys_url) {
      const isAvailable = await checkDirectusAvailability(oasys_url);
      if (!isAvailable) {
        // Redirect to fallback page if Directus is not available
        window.location.href = `${
          process.env.PUBLIC_URL || ""
        }/unavailable.html`;
        return;
      }
    }

    import("react-gtm-module").then(({ default: TagManager }) => {
      if (env === "prod") {
        TagManager.initialize({ gtmId: "GTM-NRHWGFC" });
      }
    });
  } catch (error) {
    // If we can't even get env data, redirect to fallback
    console.error("Failed to initialize app:", error);
    window.location.href = `${process.env.PUBLIC_URL || ""}/unavailable.html`;
    return;
  }
})();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
